/**
 * FragilityRadarView (P162) -- always-on concentration-risk radar.
 *
 * Where the war-room asks "what if THIS scenario hits?", the radar asks
 * "where is the estate inherently fragile?" -- ranking upstream nodes
 * (services, vendors) by blast reach x MVB-downstream concentration x
 * current open-finding stress. Transparent score, not a forecast.
 */
import { useTranslation } from "react-i18next";

import { EmptyState } from "shared/components/EmptyState";
import { useMe } from "shared/api/useMe";
import { useSearchParams } from "react-router-dom";
import { entityDetailPath } from "shared/routes/entityRoutes";
import { Bdi } from "shared/components/Bdi";
import { Link } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { Twin3DStage, Twin3DToggle, useTwin3DMode } from "twin/twin3d/Twin3DMode";
import { ToleranceBadge } from "shared/components/ToleranceBadge";
import type { ToleranceStatus } from "shared/tolerance/useToleranceState";
import type { ExposedIBS, FragilityNode } from "shared/api/types";
import { useFragility } from "twin/api/fragility_hooks";

import { FragilityChangesPanel } from "./FragilityChangesPanel";

const SEVERITY_PILL: Record<string, string> = {
  critical: "bg-rag-redBg text-rag-red",
  high: "bg-rag-amberBg text-rag-amber",
  medium: "bg-slate-100 text-slate-600",
  low: "bg-slate-100 text-slate-500",
};

function ScoreBar({ score, max }: { score: number; max: number }) {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 rounded-full bg-slate-100">
        <div
          className="h-1.5 rounded-full bg-brand-brass"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums text-slate-700">{score}</span>
    </div>
  );
}

function RadarRow({
  n,
  max,
  highlighted,
}: {
  n: FragilityNode;
  max: number;
  highlighted: boolean;
}) {
  const { t } = useTranslation();
  return (
    <tr
      data-testid={`fragility-row-${n.node_id}`}
      // A5 -- arriving from a recommendation that names this node, land
      // on the row rather than on the whole ranked estate.
      ref={(el) => {
        if (highlighted && el) el.scrollIntoView({ block: "center" });
      }}
      className={
        highlighted
          ? "border-t border-brand-brass bg-brand-brass/10"
          : "border-t border-slate-100"
      }
    >
      <td className="py-1.5 text-slate-800">
        {/* C7 -- the watchlist names the service the Resilience Lead
            came for and refused to open it, while the exposed table
            beside it links. A row that names an entity links to it. */}
        {(() => {
          const href = entityDetailPath(n.node_kind, n.node_id);
          // A kind with no detail page keeps the name and no link: a
          // link that goes nowhere is worse than none.
          return href ? (
            <Link
              to={href}
              data-testid={`fragility-node-link-${n.node_id}`}
              className="text-brand-brassDark hover:underline"
            >
              <Bdi>{n.name}</Bdi>
            </Link>
          ) : (
            <Bdi>{n.name}</Bdi>
          );
        })()}
        {" · "}
        <span className="ms-1 text-[10px] uppercase text-slate-400">{n.node_kind}</span>
      </td>
      <td className="py-1.5 tabular-nums text-slate-600">{n.blast_reach}</td>
      <td className="py-1.5 tabular-nums text-brand-brassDark">{n.mvb_downstream}</td>
      <td className="py-1.5">
        {n.open_findings > 0 ? (
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              SEVERITY_PILL[n.max_severity ?? ""] ?? "bg-slate-100 text-slate-600"
            }`}
          >
            {t("modules.twin.fragility.findingsPill", {
              count: n.open_findings,
              severity: n.max_severity ?? "",
            })}
          </span>
        ) : (
          <span className="text-slate-300">--</span>
        )}
      </td>
      <td className="py-1.5">
        <ScoreBar score={n.fragility_score} max={max} />
      </td>
    </tr>
  );
}

function ExposedRow({ e, max }: { e: ExposedIBS; max: number }) {
  const { t } = useTranslation();
  return (
    <tr
      data-testid={`fragility-exposed-${e.ibs_id}`}
      className="border-t border-slate-100"
    >
      <td className="py-1.5 text-slate-800">
        {/* B7 -- the radar named the service the Resilience Lead
            wanted and offered no route to it, on three screens. A row
            that names an entity links to it. */}
        <Link
          to={`/twin/ibs/${e.ibs_id}`}
          data-testid={`fragility-exposed-link-${e.ibs_id}`}
          className="text-brand-brassDark hover:underline"
        >
          {e.name}
        </Link>
        {e.mvb_flag && (
          <span className="ms-1 text-[10px] font-semibold uppercase text-brand-brassDark">
            {t("screens.fragilityRadarView.mvb")}
          </span>
        )}
      </td>
      <td className="py-1.5 text-slate-600">
        {e.supporting_count <= 1 ? (
          <span className="rounded bg-rag-amberBg px-1.5 py-0.5 text-xs text-rag-amber">
            {t("modules.twin.fragility.singleSupplier")}
          </span>
        ) : (
          <span className="tabular-nums">
            {t("modules.twin.fragility.supporting", { count: e.supporting_count })}
          </span>
        )}
      </td>
      <td className="py-1.5 tabular-nums text-slate-600">
        {e.binding_rto_hours === null
          ? "--"
          : t("modules.twin.fragility.rtoHours", { n: e.binding_rto_hours })}
      </td>
      {/* B1 -- the DECLARED tolerance, said the way it is said
          everywhere else. This column used to be headed "Tolerance
          (RTO)" and print the binding RTO beside it: an IBS declared at
          2h read 24h, because those are two different quantities and
          one of them had borrowed the other's name. */}
      <td className="py-1.5" data-testid={`fragility-tolerance-${e.ibs_id}`}>
        <ToleranceBadge
          state={{
            ibs_service_id: e.ibs_id,
            ibs_code: "",
            ibs_name: e.name,
            mvb: e.mvb_flag,
            hours: e.tolerance_hours ?? null,
            status: e.tolerance_status as ToleranceStatus,
            signed_by: null,
            proposed_hours: e.tolerance_proposed_hours ?? null,
            awaiting_role: e.tolerance_awaiting_role ?? null,
            awaiting_since: e.tolerance_awaiting_since ?? null,
            owner_recorded: e.tolerance_owner_recorded ?? true,
          }}
        />
      </td>
      <td className="py-1.5">
        {e.open_findings > 0 ? (
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              SEVERITY_PILL[e.max_severity ?? ""] ?? "bg-slate-100 text-slate-600"
            }`}
          >
            {t("modules.twin.fragility.findingsPill", {
              count: e.open_findings,
              severity: e.max_severity ?? "",
            })}
          </span>
        ) : (
          <span className="text-slate-300">--</span>
        )}
      </td>
      <td className="py-1.5">
        <ScoreBar score={e.exposure_score} max={max} />
      </td>
    </tr>
  );
}

export function FragilityRadarView(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const highlightNode = params.get("node");
  const radar = useFragility(20);
  const isAdmin = useMe().data?.is_admin ?? false;
  const watchlist = radar.data?.watchlist ?? [];
  const exposed = radar.data?.exposed ?? [];
  const max = watchlist.length > 0 ? watchlist[0]!.fragility_score : 0;
  const exposedMax = exposed.length > 0 ? exposed[0]!.exposure_score : 0;
  const { effectiveMode, setMode, onWebglFail, loadFailed } =
    useTwin3DMode("fragility");

  // Brief two, section 1: flipping to 3D here lands on the FRAGILITY
  // landscape. Note the 3D view scores a NEIGHBOURING question to this
  // page on different inputs -- it names its formula in its own legend
  // rather than letting a reader assume the two numbers agree.
  if (effectiveMode === "3d") {
    return (
      <section data-testid="fragility-view" className="flex flex-col gap-3">
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
              {t("modules.twin.fragility.kicker")}
            </p>
            <h1 className="font-display text-2xl text-slate-900">
              <MarsadIcon
                name="fragility-radar"
                size={22}
                className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
              />
              {t("modules.twin.fragility.heading")}
            </h1>
          </div>
          <Twin3DToggle
            mode={effectiveMode}
            onChange={setMode}
            testid="fragility-view"
            loadFailed={loadFailed}
          />
        </header>
        <Twin3DStage view="fragility" onWebglFail={onWebglFail} />
      </section>
    );
  }

  return (
    <section data-testid="fragility-view" className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
            {t("modules.twin.fragility.kicker")}
          </p>
          <h1 className="font-display text-2xl text-slate-900">
            <MarsadIcon
              name="fragility-radar"
              size={22}
              className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
            />
            {t("modules.twin.fragility.heading")}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            {t("modules.twin.fragility.intro")}
          </p>
        </div>
        <Twin3DToggle
          mode={effectiveMode}
          onChange={setMode}
          testid="fragility-view"
          loadFailed={loadFailed}
        />
      </header>

      {radar.isLoading && (
        <p data-testid="fragility-loading" className="text-sm text-slate-500">
          {t("modules.twin.fragility.loading")}
        </p>
      )}
      {radar.isError && (
        <p data-testid="fragility-error" className="text-sm text-rag-red">
          {t("modules.twin.fragility.error")}
        </p>
      )}

      {/* Round 12 A6 -- a five-column header drawn over nothing is a
          claim that the scan ran and found nothing, which is the
          opposite of what is true on a bank that has loaded no estate.
          The headline goes with it: "0 upstream nodes assessed -- 0
          already showing stress" reads as a clean result. */}
      {radar.data && watchlist.length === 0 && exposed.length === 0 && (
        <EmptyState
          testid="fragility-empty"
          message={
            t("modules.twin.fragility.empty") +
            (isAdmin ? "" : " " + t("common.emptyAskAdmin"))
          }
          cta={
            isAdmin
              ? {
                  label: t("modules.twin.fragility.emptyCta"),
                  to: "/admin/data-import",
                }
              : undefined
          }
        />
      )}

      {radar.data && (watchlist.length > 0 || exposed.length > 0) && (
        <>
          <div
            data-testid="fragility-headline"
            className="rounded-md bg-brand-brass/5 px-3 py-2 text-sm font-semibold text-brand-brassDark"
          >
            {t("modules.twin.fragility.headline", {
              assessed: radar.data.assessed,
              degraded: radar.data.degraded,
            })}
          </div>
          <article className="rounded-lg border border-slate-200 bg-white p-4">
            <table className="w-full text-start text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-1">{t("modules.twin.fragility.colNode")}</th>
                  <th className="py-1">{t("modules.twin.fragility.colBlast")}</th>
                  <th className="py-1">{t("modules.twin.fragility.colMvb")}</th>
                  <th className="py-1">{t("modules.twin.fragility.colStress")}</th>
                  <th className="py-1">{t("modules.twin.fragility.colScore")}</th>
                </tr>
              </thead>
              <tbody>
                {watchlist.map((n) => (
                  <RadarRow
                    key={n.node_id}
                    n={n}
                    max={max}
                    highlighted={n.node_id === highlightNode}
                  />
                ))}
              </tbody>
            </table>
          </article>

          {exposed.length > 0 && (
            <article
              data-testid="fragility-exposed"
              className="rounded-lg border border-slate-200 bg-white p-4"
            >
              <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
                {t("modules.twin.fragility.exposedHeading")}
                {/* B7 -- the page's one stress count sat above the
                    OTHER list. Both numbers were true; one was over
                    the wrong rows. Each header counts what is beneath
                    it. */}
                {" · "}
                {t("modules.twin.fragility.exposedStress", {
                  count: radar.data?.exposed_degraded ?? 0,
                })}
              </p>
              <table className="w-full text-start text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="py-1">{t("modules.twin.fragility.colIbs")}</th>
                    <th className="py-1">
                      {t("modules.twin.fragility.colRedundancy")}
                    </th>
                    <th className="py-1">
                      {t("modules.twin.fragility.colBindingRto")}
                    </th>
                    <th className="py-1">{t("modules.twin.fragility.colTolerance")}</th>
                    <th className="py-1">{t("modules.twin.fragility.colStress")}</th>
                    <th className="py-1">{t("modules.twin.fragility.colExposure")}</th>
                  </tr>
                </thead>
                <tbody>
                  {exposed.map((e) => (
                    <ExposedRow key={e.ibs_id} e={e} max={exposedMax} />
                  ))}
                </tbody>
              </table>
            </article>
          )}
          <FragilityChangesPanel />
        </>
      )}
    </section>
  );
}
